import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ScrollText, ArrowDownCircle, ArrowUpCircle, Trash2, Settings2,
  ShoppingBag, Truck, PackageCheck, Undo2, Warehouse, Store, Receipt, UtensilsCrossed, Radar,
} from "lucide-react";
import api from "../lib/api";
import { Card, PageHeader, PageLoader, Badge, Select } from "../lib/ui";
import { fmtNum, fmtUnit } from "../lib/constants";
import { useT } from "../lib/i18n";

const TYPE_META = {
  PURCHASE: { icon: ShoppingBag, tone: "success", label: "Purchase" },
  STOCK_IN: { icon: ArrowDownCircle, tone: "success", label: "Stock In" },
  RETURN_IN: { icon: Undo2, tone: "success", label: "Return In" },
  ADJUSTMENT_IN: { icon: Settings2, tone: "warn", label: "Adjustment +" },
  BRANCH_RECEIVE: { icon: PackageCheck, tone: "info", label: "Branch Receive" },
  DELIVERY_OUT: { icon: Truck, tone: "info", label: "Delivery Out" },
  STOCK_OUT: { icon: ArrowUpCircle, tone: "info", label: "Stock Out" },
  WASTE: { icon: Trash2, tone: "danger", label: "Waste" },
  ADJUSTMENT_OUT: { icon: Settings2, tone: "warn", label: "Adjustment −" },
  ADJUSTMENT: { icon: Settings2, tone: "warn", label: "Adjustment" },
  RETURN_TO_SUPPLIER: { icon: Undo2, tone: "danger", label: "Return to Supplier" },
  PRODUCTION_OUT: { icon: ArrowUpCircle, tone: "info", label: "Production Use" },
  PRODUCTION_IN: { icon: ArrowDownCircle, tone: "success", label: "Production Output" },
  SALES_CONSUMPTION: { icon: Receipt, tone: "warn", label: "Sales Consumption" },
  EMPLOYEE_MEAL: { icon: UtensilsCrossed, tone: "info", label: "Employee Meal" },
};

const CATEGORY_FILTERS = [
  { key: "all", label: "All" },
  { key: "purchase", label: "Purchase" },
  { key: "delivery", label: "Delivery" },
  { key: "receive", label: "Receive" },
  { key: "sales_consumption", label: "Sales Consumption" },
  { key: "employee_meal", label: "Employee Meal" },
  { key: "production", label: "Production" },
  { key: "waste", label: "Waste" },
  { key: "adjustment", label: "Adjustment" },
  { key: "return", label: "Return" },
];

const todayStr = () => new Date().toISOString().slice(0, 10);
const shiftDays = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);
const monthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };

export default function Ledger() {
  const { t } = useT();
  const navigate = useNavigate();
  const [moves, setMoves] = useState(null);
  const [category, setCategory] = useState("all");
  const [scope, setScope] = useState("all");
  const [period, setPeriod] = useState("month");
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(todayStr());

  const applyPeriod = (p) => {
    setPeriod(p);
    if (p === "today") { setFrom(todayStr()); setTo(todayStr()); }
    else if (p === "week") { setFrom(shiftDays(6)); setTo(todayStr()); }
    else if (p === "month") { setFrom(monthStart()); setTo(todayStr()); }
  };

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (category !== "all") params.set("category", category);
    if (scope !== "all") params.set("scope", scope);
    if (from) params.set("date_from", from);
    if (to) params.set("date_to", to);
    setMoves(null);
    api.get(`/stock-movements?${params.toString()}`).then(({ data }) => setMoves(data));
  }, [category, scope, from, to]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="animate-fade-up">
      <PageHeader title={t("Stock Movement Ledger")} subtitle={t("Single source of truth — every stock change, fully traceable")} icon={ScrollText} />

      {/* Category filters */}
      <div className="mb-3 flex flex-wrap gap-2">
        {CATEGORY_FILTERS.map((c) => (
          <button key={c.key} onClick={() => setCategory(c.key)} data-testid={`ledger-filter-${c.key}`}
            className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition-all ${category === c.key ? "bg-primary text-white" : "bg-white border border-line text-ink-soft hover:bg-stone-50"}`}>
            {t(c.label)}
          </button>
        ))}
      </div>

      {/* Scope + date filters */}
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Select label={t("Location")} value={scope} onChange={(e) => setScope(e.target.value)} className="w-44" data-testid="ledger-scope">
          <option value="all">{t("All Locations")}</option>
          <option value="central">{t("Central Warehouse")}</option>
          <option value="branch">{t("Branch")}</option>
        </Select>
        <div className="inline-flex overflow-hidden rounded-xl border border-line">
          {["today", "week", "month"].map((p) => (
            <button key={p} onClick={() => applyPeriod(p)} data-testid={`ledger-period-${p}`}
              className={`px-3 py-2 text-xs font-semibold capitalize transition-colors ${period === p ? "bg-primary text-white" : "bg-white text-ink-soft hover:bg-stone-50"}`}>
              {p === "week" ? t("This Week") : p === "month" ? t("This Month") : t("Today")}
            </button>
          ))}
        </div>
        <div><label className="cf-label">{t("From")}</label><input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPeriod("custom"); }} className="cf-input" data-testid="ledger-from" /></div>
        <div><label className="cf-label">{t("To")}</label><input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPeriod("custom"); }} className="cf-input" data-testid="ledger-to" /></div>
      </div>

      {!moves ? <PageLoader /> : (
        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-line text-left text-xs uppercase text-ink-mute">
                <th className="px-4 py-3">{t("When")}</th><th className="px-4 py-3">{t("Reference")}</th><th className="px-4 py-3">{t("Type")}</th>
                <th className="px-4 py-3">{t("Product")}</th><th className="px-4 py-3">{t("Location")}</th>
                <th className="px-4 py-3 text-right">{t("Qty")}</th><th className="px-4 py-3 text-right">{t("Before → After")}</th>
                <th className="px-4 py-3">{t("Reason")}</th><th className="px-4 py-3">{t("By")}</th></tr></thead>
              <tbody>
                {moves.length === 0 && <tr><td colSpan={9} className="px-5 py-10 text-center text-ink-mute">{t("No movements in this range")}</td></tr>}
                {moves.map((m) => {
                  const meta = TYPE_META[m.type] || { icon: Settings2, tone: "mute", label: m.type };
                  const Icon = meta.icon;
                  return (
                    <tr key={m.id} className="border-b border-line/60 hover:bg-stone-50/60" data-testid="ledger-row">
                      <td className="px-4 py-3 text-xs text-ink-mute">{(m.created_at || "").slice(0, 16).replace("T", " ")}</td>
                      <td className="px-4 py-3 font-mono text-xs font-semibold text-ink-soft">{m.reference_no || "—"}</td>
                      <td className="px-4 py-3"><Badge tone={meta.tone}><Icon size={13} /> {t(meta.label)}</Badge></td>
                      <td className="px-4 py-3 font-semibold">
                        {m.product_id ? (
                          <button onClick={() => navigate(`/traceability?product=${m.product_id}`)} title="Telusuri rantai pasok"
                            className="inline-flex items-center gap-1 text-left hover:text-primary" data-testid={`ledger-trace-${m.product_id}`}>
                            {m.product_name} <Radar size={12} className="text-ink-mute" />
                          </button>
                        ) : m.product_name}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1.5 text-xs text-ink-soft">
                          {m.scope === "central" ? <Warehouse size={13} /> : <Store size={13} />}{m.location}
                        </span>
                      </td>
                      <td className={`px-4 py-3 text-right font-bold ${m.direction === "in" ? "text-success-text" : "text-danger-text"}`}>{m.direction === "in" ? "+" : "−"}{fmtNum(m.quantity)} {fmtUnit(m.unit)}</td>
                      <td className="px-4 py-3 text-right text-xs text-ink-soft">{fmtNum(m.balance_before)} → <span className="font-semibold text-ink">{fmtNum(m.balance_after)}</span></td>
                      <td className="px-4 py-3 text-xs text-ink-soft">{m.reason || m.note || "—"}</td>
                      <td className="px-4 py-3 text-xs text-ink-soft">{m.created_by_name}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
